import { Building2, UserRound } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'

import type { MyOrg } from '@/api/types'
import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { HUES } from '@/lib/hues'
import { NAV_ITEMS, SETTINGS_ITEM } from '@/lib/navigation'
import { rememberOrgSlug } from '@/lib/lastOrg'

const SETTINGS_PAGES = [
  { path: 'settings', label: 'General settings' },
  { path: 'settings/voices', label: 'Voices and model' },
  { path: 'settings/members', label: 'Members and invites' },
  { path: 'settings/roles', label: 'Roles and permissions' },
  { path: 'settings/audit', label: 'Audit log' },
]

/** Whether the command menu is open; ⌘K / Ctrl K toggles it, and the top bar's search box opens it. */
export const useCommandMenu = () => {
  const [isOpen, setIsOpen] = useState(false)
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() === 'k' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault()
        setIsOpen((open) => !open)
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [])
  return { isOpen, setIsOpen }
}

/** Jump to any page or org. */
export const CommandMenu = ({
  org,
  orgs,
  isOpen,
  setIsOpen,
}: {
  org: MyOrg
  orgs: MyOrg[]
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
}) => {
  const navigate = useNavigate()

  const go = (path: string) => {
    setIsOpen(false)
    void navigate(path)
  }

  return (
    <CommandDialog open={isOpen} onOpenChange={setIsOpen} title="Go to" description="Jump to a page or an org">
      {/* shadcn's CommandDialog leaves the Command (cmdk's store) to us; without it the input crashes the page. */}
      <Command>
        <CommandInput placeholder="Go to…" />
        <CommandList>
          <CommandEmpty>Nothing matches.</CommandEmpty>
          <CommandGroup heading="Pages">
            {NAV_ITEMS.filter((item) => org.permissions.includes(item.permission)).map((item) => (
              <CommandItem key={item.path} onSelect={() => go(`/o/${org.slug}/${item.path}`)}>
                <item.icon className={HUES[item.hue].text} /> {item.label}
              </CommandItem>
            ))}
            {SETTINGS_PAGES.map((page) => (
              <CommandItem key={page.path} onSelect={() => go(`/o/${org.slug}/${page.path}`)}>
                <SETTINGS_ITEM.icon /> {page.label}
              </CommandItem>
            ))}
            <CommandItem onSelect={() => go('/account')}>
              <UserRound /> Account
            </CommandItem>
          </CommandGroup>
          {orgs.length > 1 && (
            <CommandGroup heading="Orgs">
              {orgs.map((candidate) => (
                <CommandItem
                  key={candidate.id}
                  onSelect={() => {
                    rememberOrgSlug(candidate.slug)
                    go(`/o/${candidate.slug}`)
                  }}
                >
                  <Building2 /> {candidate.name}
                </CommandItem>
              ))}
            </CommandGroup>
          )}
        </CommandList>
      </Command>
    </CommandDialog>
  )
}
